import { CircleHelp, ClipboardCheck, Film, Palette, StickyNote, Type, type LucideIcon } from "lucide-react";
import * as React from "react";

import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

const LENS_ICONS: Record<VideoProject.QaIssueCategory, LucideIcon> = {
  ON_SCREEN_AND_POST_TEXT: Type,
  STYLE_PREFERENCES: Palette,
  REQUESTS_AND_NOTES: ClipboardCheck,
  AUDIO_VISUAL: Film,
  OTHER: StickyNote,
};

export function QaLenses({
  studioReadOnly,
  canFlag,
  activeSpotlight,
  onAddFlag,
  onSpotlightChange,
}: {
  studioReadOnly: boolean;
  canFlag: boolean;
  activeSpotlight: VideoProject.QaIssueCategory | null;
  onAddFlag: (category: VideoProject.QaIssueCategory) => void;
  onSpotlightChange: React.Dispatch<React.SetStateAction<VideoProject.QaIssueCategory | null>>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {VideoProject.QaIssueCategories.map(category => {
        const meta = VideoProject.QaIssueCategoryMeta[category];
        const Icon = LENS_ICONS[category];
        const active = activeSpotlight === category;
        return (
          <Tooltip key={category}>
            <Tooltip.Trigger
              render={
                <button
                  type="button"
                  title={`${studioReadOnly || !canFlag ? meta.label : `Flag ${meta.label}`} (${meta.shortcut})`}
                  onClick={() => {
                    if (studioReadOnly || !canFlag) {
                      onSpotlightChange(current => (current === category ? null : category));
                      return;
                    }
                    onAddFlag(category);
                  }}
                  onMouseEnter={() => onSpotlightChange(category)}
                  onMouseLeave={() => onSpotlightChange(current => (current === category ? null : current))}
                  className={cn(
                    "rounded-xl border px-3 py-2 text-left transition-all",
                    !studioReadOnly && !canFlag ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                    active
                      ? "border-amber-400 bg-amber-50 dark:bg-amber-950/40"
                      : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/60 hover:shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-500 dark:hover:bg-indigo-500/10"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="flex size-6 items-center justify-center rounded bg-slate-100 font-mono text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {meta.shortcut}
                    </span>
                    <Icon className="size-3.5 text-slate-400 dark:text-slate-500" />
                    <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {meta.label}
                    </span>
                    <CircleHelp className="size-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                  </div>
                </button>
              }
            />
            <Tooltip.Content side="right" align="start" className="max-w-80 px-3 py-2.5">
              <p className="mb-1.5 font-normal">{meta.description}</p>
              <ul className="space-y-1 text-left font-normal">
                {meta.lookFor.map(item => (
                  <li key={item} className="flex gap-1.5">
                    <span className="mt-1.5 size-1 shrink-0 rounded-full bg-background/70" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Tooltip.Content>
          </Tooltip>
        );
      })}
      {studioReadOnly || canFlag ? null : (
        <p className="text-[11px] text-slate-400 dark:text-slate-500">Switch to the edited video to flag issues.</p>
      )}
    </div>
  );
}
